import ApiClient from "../helpers/ApiClient";
import OfflineStorage from "../helpers/OfflineStorage";
import OfflineQueue from "../helpers/OfflineQueue";
import { getSyncMode } from "../config/config";

export async function getOrders() {
    const isOnline = OfflineStorage.isClientOnline();

    // Collect all order IDs that have pending or completed offline settlements
    const offlineQueue = OfflineQueue.getOfflineOrders() || [];
    const settledOrderIds = new Set();
    for (const item of offlineQueue) {
        if (!item) continue;
        if (item.actionType === 'PAY_AND_COMPLETE' && item.payload && Array.isArray(item.payload.orderIds)) {
            for (const id of item.payload.orderIds) {
                if (id !== undefined && id !== null) settledOrderIds.add(String(id));
            }
        }
        if (item.status === 'settled_offline' || item.status === 'synced') {
            settledOrderIds.add(String(item.id));
        }
    }

    if (!isOnline && getSyncMode() === 'offline_sync') {
        const cached = OfflineStorage.getCachedOrders() || [];
        // Filter out any settled orders from cached table groups
        const filteredCached = (Array.isArray(cached) ? cached : []).map(tg => {
            if (!tg) return null;
            const remainingOrders = (tg.orders || []).filter(o => o && o.id && !settledOrderIds.has(String(o.id)));
            if (remainingOrders.length === 0) return null;
            return {
                ...tg,
                orders: remainingOrders,
                order_ids: (tg.order_ids || []).filter(id => id && !settledOrderIds.has(String(id)))
            };
        }).filter(Boolean);

        return {
            status: 200,
            data: filteredCached,
            _isOffline: true
        };
    }

    try {
        const res = await ApiClient.get(`/orders?_t=${Date.now()}`);
        if (res.status === 200 && Array.isArray(res.data)) {
            if (getSyncMode() === 'online_only') {
                return res;
            }
            // Keep any pending unsynced offline orders or local offline orders so they don't disappear,
            // EXCEPT those that have already been settled offline
            const pendingOffline = offlineQueue
                .filter(o => o && o.actionType === 'ORDER' && o.status !== 'synced' && o.status !== 'settled_offline' && !settledOrderIds.has(String(o.id)));
            
            // Filter out any orders from the cloud response that have been settled offline but not yet synced
            let combined = res.data.map(tg => {
                if (!tg) return null;
                const remainingOrders = (tg.orders || []).filter(o => o && o.id && !settledOrderIds.has(String(o.id)));
                if (remainingOrders.length === 0) return null;
                return {
                    ...tg,
                    orders: remainingOrders,
                    order_ids: (tg.order_ids || []).filter(id => id && !settledOrderIds.has(String(id)))
                };
            }).filter(Boolean);

            const cached = OfflineStorage.getCachedOrders() || [];

            // Collect all order IDs that exist in the cloud response
            const cloudOrderIds = new Set();
            for (const tg of combined) {
                for (const o of (tg.orders || [])) {
                    if (o && o.id !== undefined && o.id !== null) {
                        cloudOrderIds.add(String(o.id));
                    }
                }
                for (const oid of (tg.order_ids || [])) {
                    if (oid !== undefined && oid !== null) {
                        cloudOrderIds.add(String(oid));
                    }
                }
            }

            const pendingIds = new Set(pendingOffline.map(p => String(p.id)));

            // Check cached local table groups for any order that is pending or has an offline id not yet in cloud
            if (Array.isArray(cached) && cached.length > 0) {
                for (const cachedGroup of cached) {
                    if (!cachedGroup || !Array.isArray(cachedGroup.orders)) continue;

                    // Filter orders from cachedGroup that are not yet in the cloud and NOT settled
                    const missingOrders = cachedGroup.orders.filter(o => {
                        if (!o || !o.id) return false;
                        const idStr = String(o.id);
                        if (settledOrderIds.has(idStr)) return false;
                        return pendingIds.has(idStr) || (!cloudOrderIds.has(idStr) && idStr.startsWith('ord-'));
                    });

                    if (missingOrders.length === 0) continue;

                    // Find if there is an existing matching group in combined
                    const targetGroupIndex = combined.findIndex(c => 
                        (cachedGroup.table_id && c.table_id === cachedGroup.table_id) ||
                        (cachedGroup.room_name && c.room_name === cachedGroup.room_name) ||
                        (cachedGroup.table_title && c.table_title === cachedGroup.table_title)
                    );

                    if (targetGroupIndex >= 0) {
                        // Table exists in cloud, prepend/merge the missing offline orders into this table's orders
                        const existingGroup = combined[targetGroupIndex];
                        const existingOrderIds = new Set((existingGroup.orders || []).map(o => String(o.id)));
                        const ordersToAdd = missingOrders.filter(o => !existingOrderIds.has(String(o.id)));

                        if (ordersToAdd.length > 0) {
                            const mergedOrders = [...ordersToAdd, ...(existingGroup.orders || [])];
                            const mergedOrderIds = [...ordersToAdd.map(o => o.id), ...(existingGroup.order_ids || [])];
                            combined[targetGroupIndex] = {
                                ...existingGroup,
                                orders: mergedOrders,
                                order_ids: mergedOrderIds
                            };
                        }
                    } else {
                        // New table or room entirely offline, add the whole group
                        combined.unshift({
                            ...cachedGroup,
                            orders: missingOrders,
                            order_ids: missingOrders.map(o => o.id)
                        });
                    }
                }
            }

            OfflineStorage.cacheOrders(combined);
            return {
                ...res,
                data: combined
            };
        }
        return res;
    } catch (error) {
        const cached = OfflineStorage.getCachedOrders();
        if (cached && Array.isArray(cached) && cached.length > 0) {
            const filteredCached = cached.map(tg => {
                if (!tg) return null;
                const remainingOrders = (tg.orders || []).filter(o => o && o.id && !settledOrderIds.has(String(o.id)));
                if (remainingOrders.length === 0) return null;
                return {
                    ...tg,
                    orders: remainingOrders,
                    order_ids: (tg.order_ids || []).filter(id => id && !settledOrderIds.has(String(id)))
                };
            }).filter(Boolean);

            return {
                status: 200,
                data: filteredCached,
                _isOffline: true
            };
        }
        throw error;
    }
}

export async function getOrdersInit() {
    const isOnline = OfflineStorage.isClientOnline();
    if (!isOnline) {
        const cached = OfflineStorage.getCachedOrdersInit();
        if (cached) {
            return { status: 200, data: cached, _isOffline: true };
        }
        // Fallback to POS bootstrap
        const bootstrap = OfflineStorage.getCachedBootstrap();
        if (bootstrap) {
            return {
                status: 200,
                data: {
                    printSettings: bootstrap.printSettings || {},
                    storeSettings: bootstrap.storeSettings || {},
                    paymentTypes: bootstrap.paymentTypes || [],
                    categories: bootstrap.categories || []
                },
                _isOffline: true
            };
        }
    }

    try {
        const res = await ApiClient.get("/orders/init");
        if (res.status === 200 && res.data) {
            OfflineStorage.cacheOrdersInit(res.data);
        }
        return res;
    } catch (error) {
        const cached = OfflineStorage.getCachedOrdersInit() || OfflineStorage.getCachedBootstrap();
        if (cached) {
            return {
                status: 200,
                data: {
                    printSettings: cached.printSettings || {},
                    storeSettings: cached.storeSettings || {},
                    paymentTypes: cached.paymentTypes || [],
                    categories: cached.categories || []
                },
                _isOffline: true
            };
        }
        throw error;
    }
}

export async function updateKitchenOrderItemStatus(
    orderItemId,
    status,
    cancelReason = null,
    cancelQuantity = null
) {
    try {
        const response = await ApiClient.post(`/orders/update-status/${orderItemId}`, {
            status,
            cancelReason,
            cancelQuantity
        });
        return response;
    } catch (error) {
        throw error;
    }
}

export async function updateKitchenOrderItemComplementaryStatus(
    orderItemId,
    isComplementary
) {
    try {
        const response = await ApiClient.post(`/orders/update-complementary/${orderItemId}`, {
            isComplementary
        });
        return response;
    } catch (error) {
        throw error;
    }
}

/**
 * @param {Array<Number>} orderIds - list of orders to cancel
 *  */
export async function cancelKitchenOrder(
    orderIds,
    cancelReason
) {
    try {
        const response = await ApiClient.post(`/orders/cancel`, {
            orderIds,
            cancelReason
        });
        return response;
    } catch (error) {
        throw error;
    }
}

/**
 * @param {Array<Number>} orderIds - list of orders to cancel
 *  */
export async function completeKitchenOrder(
    orderIds,
) {
    try {
        const response = await ApiClient.post(`/orders/complete`, {
            orderIds
        });
        return response;
    } catch (error) {
        throw error;
    }
}

export async function getCompleteOrderPaymentSummary(orderIds) {
    const isOnline = OfflineStorage.isClientOnline();

    const calculateOfflineSummary = () => {
        try {
            const cachedOrders = OfflineStorage.getCachedOrders() || [];
            const targetIds = (Array.isArray(orderIds) ? orderIds : [orderIds]).map(String);

            // Find all matching order objects across cached cards
            const matchedOrders = [];
            for (const card of cachedOrders) {
                for (const ord of (card.orders || [])) {
                    if (targetIds.includes(String(ord.id))) {
                        matchedOrders.push(ord);
                    }
                }
            }

            const bootstrap = OfflineStorage.getCachedBootstrap() || {};
            const serviceChargeRate = Number(bootstrap.serviceCharge || bootstrap.storeSettings?.service_charge || 0);
            const bootstrapMenuItems = Array.isArray(bootstrap.menuItems) ? bootstrap.menuItems : [];
            const bootstrapTaxes = Array.isArray(bootstrap.taxes) ? bootstrap.taxes : [];

            let subtotal = 0;
            let taxTotal = 0;
            let serviceChargeTotal = 0;
            let total = 0;

            for (const ord of matchedOrders) {
                for (const it of (ord.items || [])) {
                    if (it.status === 'cancelled' || it.status === 'canceled' || it.is_complementary) continue;

                    const rawUnitPrice = (it.variant_price !== null && it.variant_price !== undefined && it.variant_price !== '')
                        ? it.variant_price
                        : (it.price !== null && it.price !== undefined ? it.price : 0);
                    const unitPrice = Number(String(rawUnitPrice).replace(/,/g, "")) || 0;
                    const qty = Number(String(it.quantity !== null && it.quantity !== undefined ? it.quantity : 1).replace(/,/g, "")) || 1;
                    const catalogPrice = Number(String(it.catalog_price || 0).replace(/,/g, "")) || 0;

                    let addonsTotal = 0;
                    if (Array.isArray(it.addons)) {
                        for (const addon of it.addons) {
                            const aPrice = Number(String(addon?.price || 0).replace(/,/g, "")) || 0;
                            addonsTotal += aPrice;
                        }
                    }

                    let effectiveUnitPrice = unitPrice;
                    if (catalogPrice > 0 && Math.abs(unitPrice - catalogPrice) < 0.01 && addonsTotal > 0) {
                        effectiveUnitPrice = unitPrice + addonsTotal;
                    } else if (unitPrice < addonsTotal && addonsTotal > 0) {
                        effectiveUnitPrice = unitPrice + addonsTotal;
                    }

                    const itemLinePrice = effectiveUnitPrice * qty;
                    let taxRate = Number(String(it.tax_rate || 0).replace(/,/g, "")) || 0;
                    let taxType = it.tax_type || "";

                    if (!taxRate && it.item_id) {
                        const catalogItem = bootstrapMenuItems.find(m => String(m.id) === String(it.item_id));
                        if (catalogItem) {
                            taxRate = Number(catalogItem.tax_rate || 0);
                            taxType = taxType || catalogItem.tax_type || "";
                            if (!taxRate && catalogItem.tax_id) {
                                const taxObj = bootstrapTaxes.find(t => String(t.id) === String(catalogItem.tax_id));
                                if (taxObj) {
                                    taxRate = Number(taxObj.rate || 0);
                                    taxType = taxType || taxObj.type || "";
                                }
                            }
                        }
                    }
                    taxType = taxType || "exclusive";

                    if (taxType === "exclusive") {
                        const tax = taxRate > 0 ? (itemLinePrice * taxRate) / 100 : 0;
                        taxTotal += tax;
                        subtotal += itemLinePrice;
                        total += (itemLinePrice + tax);
                    } else if (taxType === "inclusive") {
                        const tax = taxRate > 0 ? (itemLinePrice - (itemLinePrice * (100 / (100 + taxRate)))) : 0;
                        taxTotal += tax;
                        subtotal += (itemLinePrice - tax);
                        total += itemLinePrice;
                    } else {
                        subtotal += itemLinePrice;
                        total += itemLinePrice;
                    }
                }
            }

            if (serviceChargeRate > 0 && subtotal > 0) {
                const sc = (subtotal * serviceChargeRate) / 100;
                serviceChargeTotal += sc;
                total += sc;
            }

            return {
                status: 200,
                data: {
                    subtotal: Math.round(subtotal * 100) / 100,
                    taxTotal: Math.round(taxTotal * 100) / 100,
                    serviceChargeTotal: Math.round(serviceChargeTotal * 100) / 100,
                    total: Math.round(total * 100) / 100,
                    orders: matchedOrders
                },
                _isOffline: true
            };
        } catch (calcErr) {
            console.error("[getCompleteOrderPaymentSummary] Offline calculation error:", calcErr);
            return {
                status: 200,
                data: {
                    subtotal: 0,
                    taxTotal: 0,
                    serviceChargeTotal: 0,
                    total: 0,
                    orders: []
                },
                _isOffline: true
            };
        }
    };

    const targetIds = (Array.isArray(orderIds) ? orderIds : [orderIds]).map(String);
    const hasOfflineIds = targetIds.some(id => id.startsWith('OFF-') || id.length > 10);

    if (!isOnline || hasOfflineIds) {
        return calculateOfflineSummary();
    }

    try {
        const res = await ApiClient.post("/orders/complete-order-payment-summary", {
            orderIds
        });
        return res;
    } catch (error) {
        console.warn("[getCompleteOrderPaymentSummary] API request failed, falling back to offline summary:", error?.message);
        return calculateOfflineSummary();
    }
}

export async function getInvoiceIdFromOrderId(orderIds) {
    try {
        const res = await ApiClient.post("/orders/get-invoice-id", {
            orderIds
        });
        return res;
    } catch (error) {
        throw error;
    }
}

/**
 * @param {Array<Number>} orderIds - list of orders to cancel
 * @param {Number} subTotal
 * @param {Number} taxTotal
 * @param {Number} total
 *  */
export async function payAndCompleteKitchenOrder(
    orderIds,
    subTotal,
    taxTotal,
    serviceChargeTotal,
    total,
    selectedPaymentType,
    discount = 0,
    paidAmount,
    loyaltyRedemptionAmount = 0,
    loyaltyPointsRedeemed = 0,
    loyaltyItemsRedeemed = 0,
    multiPayments = [],
    customer_id = null,
    roundOff = 0,
    itemPrices = []
) {
    const isOnline = OfflineStorage.isClientOnline();
    const payload = {
        orderIds,
        subTotal, taxTotal, serviceChargeTotal, total,
        selectedPaymentType,
        discount,
        roundOff,
        paidAmount,
        loyaltyRedemptionAmount,
        loyaltyPointsRedeemed,
        loyaltyItemsRedeemed,
        multiPayments,
        customer_id,
        itemPrices
    };

    const handleOfflinePayAndComplete = (recId) => {
    const invoiceId = OfflineStorage.getNextOfflineInvoiceNo();
    const cachedGroups = OfflineStorage.getCachedOrders() || [];
    const targetIds = (Array.isArray(orderIds) ? orderIds : [orderIds]).map(String);
    const matchedOrders = [];
    let matchedGroup = null;
    for (const grp of cachedGroups) {
      for (const ord of (grp.orders || [])) {
        if (targetIds.includes(String(ord.id))) {
          matchedOrders.push(ord);
          if (!matchedGroup) matchedGroup = grp;
        }
      }
    }
    const firstOrd = matchedOrders[0] || matchedGroup || {};
    const nowIso = new Date().toISOString();

    // 1. Mark orders as settled in the offline queue so getOrders() never resurfaces them
    OfflineQueue.markOrdersAsSettledOffline(orderIds);
    // 2. Remove completed orders from active in-memory cached orders
    OfflineStorage.removeLocalOrder(orderIds);

    // Record in local invoices cache so InvoicesPage displays it immediately
    OfflineStorage.addLocalInvoice({
      id: recId,
      invoice_id: invoiceId,
      invoice_no: invoiceId,
      invoice_number: invoiceId,
      sub_total: subTotal,
      tax_total: taxTotal,
      service_charge_total: serviceChargeTotal,
      discount: discount,
      round_off: roundOff,
      total: total,
      paid_amount: paidAmount,
      due_amount: Math.max(0, total - (paidAmount || 0)),
      payment_type_id: selectedPaymentType,
      customer_id: customer_id || firstOrd.customer_id || null,
      name: firstOrd.customer_name || null,
      delivery_type: firstOrd.delivery_type || matchedGroup?.delivery_type || null,
      table_id: firstOrd.table_id || matchedGroup?.table_id || null,
      table_title: firstOrd.table_title || matchedGroup?.table_title || null,
      floor: firstOrd.floor || matchedGroup?.floor || null,
      created_at: nowIso,
      order_time: firstOrd.date || firstOrd.created_at || nowIso,
      order_ids: orderIds,
      orders: matchedOrders.length > 0
        ? matchedOrders.map(o => ({ order_id: o.id, token_no: o.token_no || o.id }))
        : targetIds.map(id => ({ order_id: id, token_no: id })),
      tokens: matchedOrders.length > 0
        ? matchedOrders.map(o => o.token_no || o.id).join(", ")
        : (Array.isArray(orderIds) ? orderIds.join(", ") : String(orderIds))
    });

    return {
      status: 200,
      data: {
        success: true,
        invoiceId: invoiceId,
        invoice_id: invoiceId,
        billNo: invoiceId,
        rawInvoiceId: recId,
        _isOffline: true,
        offlineId: recId,
        message: "Bill settled and saved offline!"
      }
    };
  };

  if (!isOnline && getSyncMode() === 'offline_sync') {
    const record = OfflineQueue.enqueueOfflineOrder('PAY_AND_COMPLETE', payload);
    return handleOfflinePayAndComplete(record.id);
  }

  try {
    const response = await ApiClient.post(`/orders/complete-and-pay-order`, payload);
    return response;
  } catch (error) {
    const isNetworkError = !error.response || error.message === 'Network Error' || error.code === 'ECONNABORTED';
    if (isNetworkError && getSyncMode() === 'offline_sync') {
      const record = OfflineQueue.enqueueOfflineOrder('PAY_AND_COMPLETE', payload);
      return handleOfflinePayAndComplete(record.id);
    }
    throw error;
  }
}

export async function updateOrderItemPrices(itemPrices) {
    const isOnline = OfflineStorage.isClientOnline();
    if (!isOnline) {
        try {
            const cachedOrders = OfflineStorage.getCachedOrders() || [];
            const priceMap = new Map();
            (itemPrices || []).forEach(it => {
                priceMap.set(String(it.id), Number(it.price) || 0);
            });

            const updatedCards = cachedOrders.map(card => {
                const updatedOrders = (card.orders || []).map(ord => {
                    const updatedItems = (ord.items || []).map(item => {
                        if (priceMap.has(String(item.id))) {
                            return { ...item, price: priceMap.get(String(item.id)) };
                        }
                        return item;
                    });
                    return { ...ord, items: updatedItems };
                });
                return { ...card, orders: updatedOrders };
            });

            OfflineStorage.cacheOrders(updatedCards);
            return { status: 200, data: { success: true }, _isOffline: true };
        } catch (e) {
            console.error("[updateOrderItemPrices] Offline cache update error:", e);
            return { status: 200, data: { success: true }, _isOffline: true };
        }
    }

    try {
        const response = await ApiClient.post("/orders/update-item-prices", {
            itemPrices
        });
        return response;
    } catch (error) {
        const isNetworkError = !error.response || error.message === 'Network Error' || error.code === 'ECONNABORTED';
        if (isNetworkError) {
            return { status: 200, data: { success: true }, _isOffline: true };
        }
        throw error;
    }
}

export async function getOrderTables() {
  const isOnline = OfflineStorage.isClientOnline();
  if (!isOnline) {
    const bootstrap = OfflineStorage.getCachedBootstrap();
    if (bootstrap && Array.isArray(bootstrap.storeTables)) {
      return { status: 200, data: bootstrap.storeTables, _isOffline: true };
    }
  }

  try {
    const response = await ApiClient.get('/orders/tables');
    return response;
  } catch (error) {
    const bootstrap = OfflineStorage.getCachedBootstrap();
    if (bootstrap && Array.isArray(bootstrap.storeTables)) {
      return { status: 200, data: bootstrap.storeTables, _isOffline: true };
    }
    throw error;
  }
}

export async function changeOrderTable(orderIds, newTableId) {
  try {
    const response = await ApiClient.post('/orders/change-table', { orderIds, newTableId });
    return response;
  } catch (error) { throw error; }
}

export async function mergeOrderTables(sourceTableIds, targetTableId, sourceOrderIds = []) {
  try {
    const response = await ApiClient.post('/orders/merge-tables', { sourceTableIds, targetTableId, sourceOrderIds });
    return response;
  } catch (error) { throw error; }
}

export async function splitOrderBill(originalOrderId, splits) {
  try {
    const response = await ApiClient.post('/orders/split-bill', { originalOrderId, splits });
    return response;
  } catch (error) { throw error; }
}

export async function acceptDeliveryOrder(orderId) {
  try {
    const response = await ApiClient.post(`/delivery/accept-order/${orderId}`);
    return response;
  } catch (error) { throw error; }
}

export async function transferOrderItem(orderItemId, targetTableId, transferQuantity = null) {
  try {
    const response = await ApiClient.post("/orders/transfer-item", {
      orderItemId,
      targetTableId,
      transferQuantity
    });
    return response;
  } catch (error) {
    throw error;
  }
}
